/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { memo } from 'preact/compat';
import {
  useRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useEffect,
  StateUpdater,
  Dispatch,
  MutableRef
} from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { warn } from '../utils/UNSAFE_logger';
import { allTabbableElements } from '../utils/PRIVATE_tabbableUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useAnimation, UseAnimationConfig } from '../hooks/UNSAFE_useAnimation';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { SelectionMode, ListItemRendererContext, SelectionDetail } from '../UNSAFE_Collection';
import { multiVariantStyles, styles } from './themes/BaseCardViewStyles.css';
import {
  BaseCardViewReorderableItem,
  BaseCardViewReorderableItemProps
} from './BaseCardViewReorderableItem';
import { containsKey, isSameKey, Keys } from '../utils/UNSAFE_keys';
import { getItemContext } from '../utils/PRIVATE_collectionUtils';

/**
 * Props for the BaseCardViewItem Component
 */
export type Props<K, D> = {
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  itemKey: K;
  itemData: D;
  itemIndex: number;
  isTabbable: boolean;
  isCurrentKey: boolean;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  selectedKeys: Keys<K>;
  selectionMode: SelectionMode;
  onSelectionChange?: (detail: SelectionDetail<K>) => void;
  focusBehavior: 'card' | 'content';
  setIsFocusBehaviorValid?: Dispatch<StateUpdater<boolean>>;
  initialAnimation?: 'slideUp' | 'slideDown';
  isGridLayout?: boolean;
  updateCardSize?: (width: number, height: number) => void;
  onAnimationBusyStart?: () => void;
  onAnimationBusyEnd?: () => void;
} & {
  reorderItemProps?: Omit<BaseCardViewReorderableItemProps<K>, 'children'>;
};

/**
 * The internal component used to render a single item in CardView.
 */
function BaseCardViewItem<K extends string | number, D>({
  children,
  itemKey,
  itemData,
  itemIndex,
  isTabbable,
  isCurrentKey,
  isFocused,
  isFocusRingVisible,
  selectedKeys,
  selectionMode,
  onSelectionChange,
  focusBehavior,
  setIsFocusBehaviorValid,
  initialAnimation,
  isGridLayout,
  updateCardSize,
  reorderItemProps,
  onAnimationBusyStart,
  onAnimationBusyEnd
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isItemFocused = isFocused && isFocusRingVisible;
  const variantClasses = multiVariantStyles({
    itemFocused: focusBehavior === 'card' && isItemFocused ? 'isItemFocused' : 'isNotItemFocused',
    itemInitialOpacity: initialAnimation ? 'isTransparent' : 'isNotTransparent',
    itemInGridLayout: isGridLayout ? 'isInGridLayout' : 'isNotInGridLayout'
  });
  const classes = classNames([styles.itemStyle, variantClasses]);

  /*********************************** Tabbable Mode ***********************************/

  // don't bubble Arrow Up/Down/Left/Right events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      focusBehavior === 'card' &&
      isTabbable &&
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight')
    ) {
      event.stopPropagation();
    }
  };

  /************************************ Card Size ************************************/

  const handleResize = useCallback(
    (entry: ResizeObserverEntry) => {
      updateCardSize?.(entry.contentRect.width, entry.contentRect.height);
    },
    [updateCardSize]
  );

  const nullRef = useRef<HTMLDivElement>(null);
  useResizeObserver(updateCardSize ? rootRef : nullRef, handleResize);

  /********************************* Focus Behavior *********************************/

  useLayoutEffect(() => {
    if (focusBehavior === 'content' && isItemFocused) {
      // rootRef is cardview item, its first child is the focus trap div, so its first grand child
      // is the actual content
      const content = rootRef.current?.firstElementChild?.firstElementChild as HTMLElement;
      const focusable = allTabbableElements(content, true, true);
      if (focusable.length === 0 || focusable.length > 1) {
        warn(
          `${focusable.length} focusable elements are detected in the card. 'content' focusBehavior is intended only for use when the card has single focusable element. Please specify one focusable element in the card or switch the focusBehavior to 'card'.`
        );
        setIsFocusBehaviorValid?.(false);
      } else {
        focusable[0].focus();
      }
    }
  }, [focusBehavior, isItemFocused, setIsFocusBehaviorValid]);

  /******************************** Initial Animation ********************************/

  // animation for slide up/down
  const { nodeRef: nodeRefSlide, controller: controllerSlide } = useAnimation<
    'slideUp' | 'slideDown' | 'none',
    HTMLDivElement
  >(initialAnimation || 'none', animationConfigSlide(itemIndex));

  // animation for opacity change
  const { nodeRef: nodeRefOpacity, controller: controllerOpacity } = useAnimation<
    'opacity' | 'none',
    HTMLDivElement
  >(
    (initialAnimation && 'opacity') || 'none',
    animationConfigOpacity(itemIndex, timeoutRef, onAnimationBusyEnd)
  );

  if (initialAnimation) {
    onAnimationBusyStart?.();
  }

  // max cap for animation is 1s
  useEffect(() => {
    if (initialAnimation !== undefined) {
      timeoutRef.current = setTimeout(() => {
        controllerSlide.cancel();
        controllerOpacity.cancel();
        if (rootRef.current) {
          rootRef.current.style.opacity = '1';
        }
        onAnimationBusyEnd?.();
      }, 1000);
    }
  }, [controllerOpacity, controllerSlide, initialAnimation, onAnimationBusyEnd]);

  // TODO: issue track in JET-57681, we currently have two useAnimation() because the opacity and
  // slide up/down have different durations, and the current API couldn't handle this case, we will
  // update this once the API is updated

  /********************************** Child Component **********************************/
  const itemRefs = useMemo(() => {
    // NOTE: spot an issue when we click the cards during animation is processing, the cards would not finish
    // the current animations and stay as what it is, e.g. leave itself as half transparent.
    // need to remove nodeRefSlide and nodeRefOpacity to avoid this issue.
    if (initialAnimation === undefined) {
      return mergeRefs(rootRef);
    }
    return mergeRefs(rootRef, nodeRefSlide, nodeRefOpacity);
  }, [rootRef, nodeRefSlide, nodeRefOpacity, initialAnimation]);

  const rendererContext = getItemContext<K, D>(
    { data: { data: itemData, metadata: { key: itemKey } }, index: itemIndex },
    selectedKeys,
    selectionMode === 'multiple',
    onSelectionChange
  );

  const childrenComponent = reorderItemProps ? (
    <BaseCardViewReorderableItem
      {...reorderItemProps}
      itemKey={itemKey}
      itemIndex={itemIndex}
      isCurrentKey={isCurrentKey}>
      {children(rendererContext)}
    </BaseCardViewReorderableItem>
  ) : (
    children(rendererContext)
  );

  return (
    <div
      id={useId()}
      ref={itemRefs}
      class={classes}
      role="gridcell"
      data-oj-card-item=""
      data-oj-key={itemKey}
      onKeyDown={handleKeyDown}
      aria-selected={selectionMode === 'none' ? undefined : containsKey(selectedKeys, itemKey)}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
          {childrenComponent}
        </FocusTrap>
      </TabbableModeContext.Provider>
    </div>
  );
}

const animationConfigSlide = (
  index: number
): UseAnimationConfig<'slideUp' | 'slideDown' | 'none', HTMLDivElement> => {
  return {
    animationStates: {
      slideUp: {
        from: {
          translateY: '50px'
        },
        to: {
          translateY: '0px'
        },
        options: {
          duration: 300,
          delay: index * 50,
          easing: [0, 0, 0.2, 1]
        }
      },
      slideDown: {
        from: {
          translateY: '-50px'
        },
        to: {
          translateY: '0'
        },
        options: {
          duration: 300,
          delay: index * 50,
          easing: [0, 0, 0.2, 1]
        }
      }
    },
    isAnimatedOnMount: true
  };
};

const animationConfigOpacity = (
  index: number,
  timeoutRef: MutableRef<ReturnType<typeof setTimeout> | undefined>,
  onAnimationBusyEnd?: () => void
): UseAnimationConfig<'opacity' | 'none', HTMLDivElement> => {
  return {
    animationStates: {
      opacity: {
        from: {
          opacity: 0
        },
        to: {
          opacity: 1
        },
        options: {
          duration: 150,
          delay: index * 50,
          easing: 'linear'
        }
      }
    },
    isAnimatedOnMount: true,
    onAnimationEnd: () => {
      onAnimationBusyEnd?.();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };
};

/**
 * Compare the equality between two reorderItemProps, especially useful for memoized items
 */
const _compareReorderItemProps = (
  props1: Props<string | number, unknown>['reorderItemProps'],
  props2: Props<string | number, unknown>['reorderItemProps']
) => {
  return (
    props1?.['dragKey'] === props2?.['dragKey'] &&
    props1?.['setDragKey'] === props2?.['setDragKey'] &&
    props1?.['columns'] === props2?.['columns'] &&
    props1?.['gutterSize'] === props2?.['gutterSize'] &&
    props1?.['rootRef'] === props2?.['rootRef']
  );
};

export const MemoizeBaseCardViewItem = memo(
  BaseCardViewItem,
  (prev: Props<string | number, unknown>, next: Props<string | number, unknown>) => {
    if (prev && next) {
      return (
        prev.children === next.children &&
        prev.itemKey === next.itemKey &&
        prev.itemData === next.itemData &&
        prev.itemIndex === next.itemIndex &&
        prev.isTabbable === next.isTabbable &&
        prev.isCurrentKey === next.isCurrentKey &&
        prev.isFocused === next.isFocused &&
        prev.isFocusRingVisible === next.isFocusRingVisible &&
        prev.initialAnimation === next.initialAnimation &&
        prev.focusBehavior === next.focusBehavior &&
        isSameKey(prev.selectedKeys, next.selectedKeys) &&
        prev.selectionMode === next.selectionMode &&
        prev.onSelectionChange === next.onSelectionChange &&
        prev.setIsFocusBehaviorValid === next.setIsFocusBehaviorValid &&
        prev.isGridLayout === next.isGridLayout &&
        prev.updateCardSize === next.updateCardSize &&
        prev.onAnimationBusyStart === next.onAnimationBusyStart &&
        prev.onAnimationBusyEnd === next.onAnimationBusyEnd &&
        _compareReorderItemProps(prev.reorderItemProps, next.reorderItemProps)
      );
    }
    return false;
  }
);
